/* eslint-disable @typescript-eslint/no-explicit-any */
// One conversation with the care team — the turns, and the box to add another. URS_12.1.
//
// ⭐ THE READ RECEIPT IS SENT FROM HERE AND NOWHERE ELSE. The kernel keeps it a separate call
// from fetching the thread precisely so that a list prefetch cannot tell a clinician "the
// patient has seen your reply". This screen is the only place that claim is true, so this is
// the only place that makes it.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  IonContent,
  IonPage,
  IonSkeletonText,
  IonTextarea,
  IonButton,
  IonToast,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  useIonRouter,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import { App } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import Header from "../../components/Header";
import { useHaptics } from "../../hooks/useHaptics";
import { useEngagementFeatures } from "../../hooks/useEngagementFeatures";
import {
  MessageThread,
  engagementErrorMessage,
  markThreadRead,
  readMyThread,
  replyInThread,
} from "../../services/engagement";
import { formatWhen } from "./CareTeamMessages";
import "./Engagement.css";

// F-195 — a portal reply otherwise only appeared once the patient left this screen and came
// back. There is no push infrastructure, so this is a poll: about every 5s (F-234 brought this
// down from the original 10s — a reply could take up to 10s to show, which is what F-195 asked
// to fix in the first place) while the thread is actually on screen (never in the background —
// see the ⛔ on `markThreadRead` above).
const CONVERSATION_POLL_MS = 5000;

const CareTeamThread: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const router = useIonRouter();
  const { successActivity, errorFeedback } = useHaptics();
  const { features, isLoading: featuresLoading } = useEngagementFeatures();

  const [thread, setThread] = useState<MessageThread | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Guards against overlapping requests: the mount load, the poll tick, the pull-to-refresh
  // and the post-send reload can all fire close together, and a slow response must not stack a
  // second one behind it (which would also race two `setThread` calls out of order).
  const isLoadingRef = useRef(false);
  // Whether this screen is actually the one on screen right now. Starts true because the first
  // render IS the patient opening the thread; useIonViewDidLeave/WillEnter (fired by the Ionic
  // router when this cached view is navigated away from and back to) keep it current after
  // that. The poll and the app-foreground refresh both gate on this — never on the background.
  const isActiveRef = useRef(true);
  const isMountedRef = useRef(true);

  const load = useCallback(
    async (sendReceipt: boolean) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      try {
        const data = await readMyThread(Number(threadId));
        if (!isMountedRef.current) return;
        setThread(data);
        if (sendReceipt) {
          // Fire-and-forget: a failed receipt must never stop the patient reading. The kernel
          // is idempotent, so the next open re-tries it for free.
          //
          // ⭐ E12d/R1 — THIS IS WHAT CLEARS THE BADGE, on the thread row and on the Messages
          // entry alike. Both read `read_at` on the staff turns, which is the column this
          // stamps, so there is no second piece of state for the app to keep in step.
          void markThreadRead(Number(threadId)).catch(() => undefined);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(engagementErrorMessage(err, "This conversation could not be opened."));
        }
      } finally {
        if (isMountedRef.current) setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [threadId],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages.length]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Poll while mounted; the ref guards mean a tick that lands while the screen is backgrounded
  // (or another load is already in flight) is simply skipped rather than queued. Re-fetching an
  // unchanged thread never moves the scroll position — the effect above only scrolls when the
  // message count itself changes.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (isActiveRef.current) void load(true);
    }, CONVERSATION_POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  // Ionic keeps this screen mounted in the view stack when the patient navigates to another
  // tab/page, so a plain unmount effect cannot tell "left" from "backgrounded". These fire on
  // every enter/leave of the cached view, including the first one, so they double as an
  // immediate refresh on return rather than waiting for the next poll tick.
  useIonViewWillEnter(() => {
    isActiveRef.current = true;
    void load(true);
  });
  useIonViewDidLeave(() => {
    isActiveRef.current = false;
  });

  // Refresh immediately when the OS brings the app back to the foreground while this screen is
  // the one on screen — otherwise a reply sent while the phone was locked waits for the next
  // poll tick.
  useEffect(() => {
    let handle: PluginListenerHandle | undefined;
    let cancelled = false;

    void App.addListener("appStateChange", (state) => {
      if (state.isActive && isActiveRef.current) {
        void load(true);
      }
    }).then((added) => {
      if (cancelled) {
        void added.remove();
      } else {
        handle = added;
      }
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [load]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await load(true);
    event.detail.complete();
  };

  const send = async () => {
    if (!reply.trim() || isSending) return;
    setIsSending(true);
    try {
      await replyInThread(Number(threadId), reply);
      setReply("");
      await successActivity();
      await load(false);
    } catch (err) {
      await errorFeedback();
      setError(engagementErrorMessage(err, "Your reply could not be sent."));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <IonPage>
      {/* F-488 (UAT3 follow-up to F-481) — same header treatment as the Messages list one tap
          back: `Header` defaults `showGreeting`/`showUserIcon` to true, which put "Welcome," and
          the stacked-avatar row above the conversation title. Both off, and the same
          `engagement-messages-header` wrapper closes up the space they reserved. */}
      <div className="engagement-messages-header">
        <Header
          showGreeting={false}
          showUserIcon={false}
          showPageTitle
          pageTitle={thread?.subject || "Care team"}
          showBackButton
          onBackButtonClick={() => router.goBack()}
        />
      </div>
      <IonContent className="engagement-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {isLoading ? (
          <div className="engagement-thread">
            {[0, 1, 2].map((i) => (
              <IonSkeletonText key={i} animated style={{ height: 56, borderRadius: 16 }} />
            ))}
          </div>
        ) : (
          <div className="engagement-thread">
            {thread?.messages.map((message) => {
              const mine = message.sender.code === "patient";
              return (
                <div
                  key={message.id}
                  className={`engagement-bubble ${mine ? "is-mine" : "is-theirs"}`}
                >
                  <div className="engagement-bubble-who">{message.sender.label}</div>
                  <div className="engagement-bubble-body">{message.body}</div>
                  <div className="engagement-bubble-when">{formatWhen(message.sent_at)}</div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}

        {/* URS_12.5 AC 3 — history stays readable when messaging is switched off; only the
            composer goes away.
            F-490 (Leah, UAT3) — until the kernel has answered, the hook's `features` is
            NO_ENGAGEMENT_FEATURES (everything off), so branching on `messaging_enabled` alone
            flashed "has turned messaging off" on every open while messaging was ON. While the
            setting is unknown show a neutral placeholder: neither the composer nor the notice. */}
        {featuresLoading ? (
          <div className="engagement-composer" data-testid="engagement-composer-loading">
            <IonSkeletonText animated style={{ height: 40, borderRadius: 12, flex: 1 }} />
          </div>
        ) : features.messaging_enabled ? (
          <div className="engagement-composer">
            <IonTextarea
              placeholder="Write a reply…"
              autoGrow
              rows={1}
              value={reply}
              onIonInput={(e) => setReply(String(e.detail.value ?? ""))}
            />
            <IonButton disabled={!reply.trim() || isSending} onClick={send}>
              {isSending ? "…" : "Send"}
            </IonButton>
          </div>
        ) : (
          <div className="engagement-composer-off">
            {features.org_name ?? "Your care team"} has turned messaging off. You can still read
            this conversation.
          </div>
        )}

        <IonToast
          isOpen={!!error}
          message={error ?? ""}
          duration={5000}
          color="danger"
          onDidDismiss={() => setError(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default CareTeamThread;
